import { useMemo } from 'react';
import { dateToApiFormat } from '../../helpers/dateHelper';
import { useGetEmployeesByLocationsQuery } from '../gql/graphql.generated';
import { useLocationService } from '../location/LocationService';

interface EmployeeListOptions {
  includeDeleted?: boolean;
  date?: Date;
  specifiedLocation?: number;
}

export const useEmployeeList = (options?: EmployeeListOptions) => {
  const { includeDeleted, date, specifiedLocation } = options || {};
  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);
  const locationId = specifiedLocation ?? selectedLocationId;
  const [{ data }, refetch] = useGetEmployeesByLocationsQuery({
    variables: { locationIds: locationId ? [locationId] : [] },
    pause: !locationId
  });

  const employees = useMemo(() => {
    const employees = data?.employeesByLocations?.employees?.filter(employee => {
      if (!includeDeleted && !employee.isRosterable) {
        return false;
      }

      if (date && employee.leaveDate && dateToApiFormat(date) > dateToApiFormat(new Date(employee.leaveDate))) {
        return false;
      }

      return true;
    });

    const sortedEmployees = employees?.sort((a, b) => {
      if (a.employeeId && !b.employeeId) {
        return -1;
      } else if (!a.employeeId && b.employeeId) {
        return 1;
      } else if (a.employeeId && b.employeeId) {
        return a.employeeId > b.employeeId ? 1 : -1;
      }

      return a.hireDate > b.hireDate ? 1 : -1;
    });

    return sortedEmployees;
  }, [includeDeleted, date, data]);

  const homeEmployees = useMemo(() => {
    return (employees || []).filter(employee => employee.homeLocationId === locationId);
  }, [employees, locationId]);

  return {
    homeEmployees: homeEmployees,
    allEmployees: employees || [],
    refetch
  };
};
